import ReportContentType from "../enums/ReportContentType";

// ─── Feedbacks visual language ──────────────────────────────────────────────
// A fresh "teal" theme used only by the Feedbacks page and its grid / filter /
// detail modal — deliberately distinct from the rest of the admin panel.

export const FB = {
  // Brand accent (teal)
  accent: "#0F6E56",
  accentMid: "#1D9E75",
  accentSoft: "#E1F5EE",
  accentBorder: "#9FE1CB",

  // Surfaces / text / borders
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#e5e7eb",
  borderStrong: "#d1d5db",
  text: "#111827",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",

  // Status
  open: "#92400e",
  openSoft: "#fef3c7",
  openBorder: "#fcd34d",
  done: "#3b6d11",
  doneSoft: "#eaf3de",
  doneBorder: "#c0dd97",
  danger: "#a32d2d",
  dangerSoft: "#fcebeb",
  dangerBorder: "#f09595",

  // Dev / Prod (match the existing badge colors)
  dev: "#185fa5",
  devSoft: "#e6f1fb",
  prod: "#188038",
  prodSoft: "#e8f5e9",
};

// Turkish thousands grouping: 45000 -> "45.000".
export const fmtNum = (n) => Number(n || 0).toLocaleString("tr-TR");

// Short Turkish date: "28 Haz 2026". Falls back to the raw value if unparseable.
export const fmtDate = (s) => {
  if (s === null || s === undefined || s === "") return "-";
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Urgency tier from a report count — drives the flame + red accent on rows.
// Thresholds are intentionally simple; tune freely.
export const urgencyTier = (count) => {
  const c = Number(count) || 0;
  if (c >= 20) return "high";
  if (c >= 8) return "mid";
  return "low";
};

// ─── Columns ────────────────────────────────────────────────────────────────
// Curated column sets per report type (key → friendly label + cell type). The
// row "content" array from util/getRows is aligned with Object.keys(row), so each
// def carries the resolved content index. Report types without a curated set fall
// back to a generic, name-based classification.

const classifyColumn = (key) => {
  const k = String(key).toLowerCase();
  if (/path|url/.test(k)) return "hidden";
  if (/count|adet|rapor|report/.test(k)) return "count";
  if (/date|tarih|zaman/.test(k)) return "date";
  if (/answeroption|cevap/.test(k)) return "answer";
  if (/status|durum/.test(k)) return "status";
  return "text";
};

const CURATED = {
  [ReportContentType.BookSectionCrop]: [
    { key: "BookSectionCropId", label: "BookSection Crop ID", type: "id" },
    { key: "QuestionNumber", label: "Soru", type: "id" },
    { key: "BookSectionId", label: "BookSectionId", type: "id" },
    { key: "AnswerOption", label: "Cevap", type: "answer" },
    { key: "TotalReportCount", label: "Rapor", type: "count" },
    { key: "MaxDate", label: "Son rapor", type: "date" },
    { key: "Status", label: "Durum", type: "status" },
  ],
};

export const getColumnDefs = (reportContentType, rawColumns) => {
  const cols = rawColumns || [];
  const curated = CURATED[reportContentType];
  if (curated) {
    return curated
      .map((d) => ({ ...d, index: cols.indexOf(d.key) }))
      .filter((d) => d.index >= 0);
  }
  return cols
    .map((key, index) => ({ key, label: key, type: classifyColumn(key), index }))
    .filter((d) => d.type !== "hidden");
};

// "1- Konu Yanlış   ||   2- Soru Eksik" -> ["Konu Yanlış", "Soru Eksik"].
export const cleanReasons = (description) => {
  if (!description) return [];
  return String(description)
    .split("||")
    .map((s) => s.replace(/^\s*\d+\s*-\s*/, "").trim())
    .filter(Boolean);
};
